"use client"

import React, { useRef, useState, useCallback, useEffect } from "react"

interface CompareSliderProps {
  itemOne: React.ReactNode
  itemTwo: React.ReactNode
  /** Initial position as a percentage (0–100). Defaults to 50. */
  defaultPosition?: number
  /** Accessible label for the slider thumb. */
  "aria-label"?: string
}

export default function CompareSlider({
  itemOne,
  itemTwo,
  defaultPosition = 50,
  "aria-label": ariaLabel = "Comparison slider",
}: CompareSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState(defaultPosition)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)
  const dragging = useRef(false)

  const clamp = (v: number) => Math.min(100, Math.max(0, v))

  const updatePosition = useCallback((clientX: number) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    const pct = ((clientX - rect.left) / rect.width) * 100
    setPosition(clamp(pct))
  }, [])

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      dragging.current = true
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
      updatePosition(e.clientX)
    },
    [updatePosition],
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return
      updatePosition(e.clientX)
    },
    [updatePosition],
  )

  const handlePointerUp = useCallback(() => {
    dragging.current = false
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 10 : 1
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault()
      setPosition(prev => clamp(prev - step))
    } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault()
      setPosition(prev => clamp(prev + step))
    }
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const prevent = (e: TouchEvent) => {
      if (dragging.current) e.preventDefault()
    }
    el.addEventListener("touchmove", prevent, { passive: false })

    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width)
    })
    ro.observe(el)

    return () => {
      el.removeEventListener("touchmove", prevent)
      ro.disconnect()
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className="compare-slider relative select-none overflow-hidden"
      style={{ touchAction: "pan-y" }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {/* Bottom layer — item two (fully visible) */}
      <div className="relative w-full" aria-hidden="true">
        {itemTwo}
      </div>

      {/* Top layer — item one, clipped to the left portion */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${position}%` }}
        aria-hidden="true"
      >
        <div className="h-full" style={{ width: containerWidth ?? "100%" }}>
          {itemOne}
        </div>
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white"
        style={{ left: `${position}%`, transform: "translateX(-50%)" }}
      >
        {/* Shadow for contrast against both light and dark images */}
        <div className="absolute inset-0 shadow-[0_0_4px_rgba(0,0,0,0.5)]" />
      </div>

      {/* Draggable thumb */}
      <div
        role="slider"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-valuenow={Math.round(position)}
        aria-valuemin={0}
        aria-valuemax={100}
        onKeyDown={handleKeyDown}
        className="
          absolute top-1/2 z-10
          flex h-10 w-10 -translate-x-1/2 -translate-y-1/2
          cursor-ew-resize items-center justify-center
          rounded-full bg-white shadow-md
          outline-none ring-trimble-blue focus-visible:ring-2
        "
        style={{ left: `${position}%` }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-5 w-5 text-gray-600"
        >
          <polyline points="8 4 4 8 8 12" />
          <polyline points="16 4 20 8 16 12" />
        </svg>
      </div>
    </div>
  )
}

export function CompareSliderImage({
  src,
  alt,
  ...rest
}: React.ImgHTMLAttributes<HTMLImageElement>) {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={alt ?? ""}
      draggable={false}
      className="block h-auto w-full"
      {...rest}
    />
  )
}
